import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { SearchableCombobox, TSearchable } from '../base/combobox'
import { AccessorySelectorQuery, SelectedAccessorySelectorQuery } from './__generated__/selector_query'
import { Query, SelectedQuery } from './selector_query'
import { dedupe } from '@/helpers/dedupe'
import { useCombobox } from '@/helpers/useCombobox'

type AssetType = SelectedAccessorySelectorQuery['assetTypes']['nodes'][0]
type RowItem = SelectedAccessorySelectorQuery['rowItems']['nodes'][0]

interface Props {
  value: string
  onChange: (value: TSearchable | null) => void
  invalid: boolean
}

export const Selector = ({ value, onChange, invalid }: Props) => {
  const { data: selectedData } = useQuery<AccessorySelectorQuery>(
    SelectedQuery,
    { variables: { id: value || 'blank' } }
  )
  const { data, loading, refetch, fetchMore } = useQuery<AccessorySelectorQuery>(Query, { variables: { first: 10 } })

  const buildSearchable = (item: AssetType | RowItem) => ({
    id: `${item.__typename}:${item.id}`,
    name: item.name,
    image: item.images[0]?.url || data?.companies.nodes[0]?.placeholderLogo?.url
  })

  const { pageInfo: assetTypePageInfo, items: assetTypes } = useCombobox<AssetType>({
    pageInfo: data?.assetTypes.pageInfo,
    querySelected: selectedData?.assetTypes.nodes[0],
    queryCollection: data?.assetTypes.nodes,
    buildSearchable
  })

  const { pageInfo: rowItemPageInfo, items: rowItems } = useCombobox<RowItem>({
    pageInfo: data?.rowItems.pageInfo,
    querySelected: selectedData?.rowItems.nodes[0],
    queryCollection: data?.rowItems.nodes,
    buildSearchable
  })

  const [items, setItems] = useState<TSearchable[]>([])

  useEffect(() => {
    if (!data) return

    setItems(
      dedupe([
        ...assetTypes,
        ...rowItems,
      ]).sort((a, b) => ('' + a.name).localeCompare(b.name))
    )
  }, [data, assetTypes, rowItems])

  return (
    <SearchableCombobox
      data={items}
      filterFields={['assetTypeFilters', 'rowItemFilters']}
      includeBlank={false}
      invalid={invalid}
      loading={loading}
      placeholder="What's next on the list? Start typing to search"
      refetch={refetch}
      searchField="nameCont"
      value={items.find(i => i.id == value) || { id: '', name: '' }}
      fetchMore={() => {
        const allItems = {
          assetType: assetTypes.length,
          rowItem: rowItems.length,
        }
        const maxItems = Object.entries(allItems)
          .reduce((most, [key, count]) => count > allItems[most]
            ? key as keyof typeof allItems
            : most as keyof typeof allItems, 'assetType' as keyof typeof allItems)
        const pageInfos = {
          assetType: assetTypePageInfo,
          rowItem: rowItemPageInfo,
        }

        return fetchMore({
          variables: { after: pageInfos[maxItems].current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.assetTypes.nodes = dedupe([
              ...previousData.assetTypes.nodes,
              ...fetchMoreResult.assetTypes.nodes
            ])
            fetchMoreResult.rowItems.nodes = dedupe([
              ...previousData.rowItems.nodes,
              ...fetchMoreResult.rowItems.nodes
            ])
            return { ...fetchMoreResult }
          }
        })
      }}
      moreToFetch={
        assetTypePageInfo.current?.hasNextPage
        || rowItemPageInfo.current?.hasNextPage
      }
      onChange={onChange}
    />
  )
}
